import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi.tsx';

import type { RefereeExclusiveReward_cardSet } from '#src/components/series/inviteFriends/RefereeExclusiveReward/__generated__/index.graphql.ts';
import { RefereeExclusiveReward } from '#src/components/series/inviteFriends/RefereeExclusiveReward/index.tsx';

import {
  NewChapterOverlay,
  VIEW_TRANSITION_NAME,
} from '../NewChapterOverlay/index.tsx';
import { FireTile } from './FireTile/index.tsx';
import type {
  claimTasksTrckMutation,
  claimTasksTrckMutationVariables,
} from './__generated__/index.graphql.ts';

type NextTrckTileProps = {
  isLocked: boolean;
  onClick?: () => void;
  animated?: boolean;
  style?: CSSProperties;
  viewTransitionName?: string;
  ariaLabel?: string;
};

type Props = {
  taskId: string;
  style?: CSSProperties;
  onTrckClaimed: () => Promise<void>;
  disabled?: boolean;
  overlayContent?: {
    title: ReactNode;
    subtitle: ReactNode;
  };
  Tile?: ComponentType<NextTrckTileProps>;
  backgroundImage?: string;
  cardSet?: Nullable<RefereeExclusiveReward_cardSet>;
};

const CLAIM_TASKS_TRCK_MUTATION = gql`
  mutation claimTasksTrckMutation($taskId: ID!, $sport: Sport!) {
    claimTask(input: { taskId: $taskId }) {
      cardSet {
        id
        replayFinished(sport: $sport)
        currentSet(sport: $sport) {
          slug
          ...RefereeExclusiveReward_cardSet
        }
      }
      currentUser {
        slug
        ...RefereeExclusiveReward_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${RefereeExclusiveReward.fragments.cardSet}
  ${RefereeExclusiveReward.fragments.currentUser}
` as TypedDocumentNode<claimTasksTrckMutation, claimTasksTrckMutationVariables>;

export const NextTrckTile = ({
  taskId,
  style,
  onTrckClaimed,
  disabled,
  overlayContent,
  Tile = FireTile,
  backgroundImage,
  cardSet,
}: Props) => {
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const [claimTasksTrck] = useMutation(CLAIM_TASKS_TRCK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [overlay, setOverlay] = useState<
    'newChapter' | 'refereeReward' | undefined
  >();
  const { updateDOM } = useTransitionApi();
  const animateSetOverlay = (v: typeof overlay) => {
    updateDOM(() => {
      setOverlay(v);
    });
  };
  const [currentUser, setCurrentUser] =
    useState<
      Nullable<NonNullable<claimTasksTrckMutation['claimTask']>['currentUser']>
    >(null);
  const [claimedCardSet, setClaimedCardSet] =
    useState<Nullable<RefereeExclusiveReward_cardSet>>(null);

  const finishCb = async () => {
    await onTrckClaimed();
    animateSetOverlay(undefined);
  };

  const onClick = async () => {
    animateSetOverlay('newChapter');

    const [claimTaskResult] = await Promise.allSettled([
      claimTasksTrck({
        variables: {
          taskId,
          sport,
        },
      }),
      // Displaying new chapter unlocked screen for at least 3 seconds
      new Promise(resolve => setTimeout(resolve, 3000)),
    ]);
    const claimTaskData =
      claimTaskResult.status === 'fulfilled'
        ? claimTaskResult.value.data?.claimTask
        : null;
    const currentUserResult = claimTaskData?.currentUser;
    if (
      currentUserResult?.inviteTaskAsReferee?.aasmState ===
      ManagerProgressionTaskState.COMPLETED
    ) {
      setCurrentUser(currentUserResult);
      setClaimedCardSet(claimTaskData?.cardSet?.currentSet);
      animateSetOverlay('refereeReward');
    } else {
      await finishCb();
    }
  };

  if (disabled) {
    return <Tile isLocked style={style} />;
  }

  return (
    <>
      <Tile
        isLocked={false}
        onClick={onClick}
        style={style}
        ariaLabel={formatMessage({ defaultMessage: 'Claim next chapter' })}
        viewTransitionName={
          overlay === 'newChapter' ? undefined : VIEW_TRANSITION_NAME
        }
      />
      {overlay === 'newChapter' && (
        <NewChapterOverlay
          {...(overlayContent ?? {})}
          backgroundImage={backgroundImage}
        />
      )}
      <Dialog
        open={overlay === 'refereeReward'}
        onClose={finishCb}
        maxWidth="xs"
        fullWidth
        hideHeader
      >
        <RefereeExclusiveReward
          onClose={finishCb}
          currentUser={currentUser}
          cardSet={cardSet ?? claimedCardSet}
        />
      </Dialog>
    </>
  );
};
